import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, CheckCircle2, RotateCcw, BadgeCheck, MapPin, Loader2, Inbox, BadgePercent, Ticket, Clock, Send, CalendarClock, QrCode, IndianRupee, AlertCircle, Eye, Gift, Copy, BellRing, Menu } from "lucide-react";
import { AssignmentChat } from "@/components/AssignmentChat";

export default function BrokerDashboard() {
  const { user } = useAuth();
  const { t } = useLang();
  const { feature } = useSettings();
  const navigate = useNavigate();
  const SECTION_TITLES = { active: "Active Listings", sold: "Sold Listings", enquiries: "Enquiries", payments: "Payments", offers: "Offers" };
  const [props, setProps] = useState([]);
  const [navOpen, setNavOpen] = useState(false);
  const [tab, setTab] = useState("active");
  const [enquiries, setEnquiries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [payInfo, setPayInfo] = useState({ payments: [], subscription_active: false, subscription_expires: "" });
  const [settings, setSettings] = useState({ upi_id: "", upi_qr: "", per_listing_fee: 200, monthly_subscription: 200 });
  const isOwner = user.role === "owner";
  const [offerForm, setOfferForm] = useState({ title: "", description: "", coupon_code: "", discount: "", area_name: "", pincode: "", city: "" });
  const [respondEnq, setRespondEnq] = useState(null);
  const [respText, setRespText] = useState("");
  const [respDate, setRespDate] = useState("");
  const [referral, setReferral] = useState({ referral_code: "", count: 0, referrals: [] });
  const [chatUnread, setChatUnread] = useState({ total: 0, by_enquiry: {} });
  const prevChatUnread = useRef(0);
  const chatInit = useRef(false);
  const [loading, setLoading] = useState(true);

  const load = async (spinner = false) => {
    if (spinner) setLoading(true);
    const [p, e, o] = await Promise.all([api.get("/broker/properties"), api.get("/enquiries"), api.get("/offers/mine")]);
    setProps(p.data.properties);
    setEnquiries(e.data.enquiries);
    setOffers(o.data.offers);
    api.get("/payments/mine").then((r) => setPayInfo(r.data)).catch(() => {});
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    if (!isOwner) api.get("/broker/referrals").then((r) => setReferral(r.data)).catch(() => {});
    api.get("/enquiries/chat/unread").then((r) => {
      if (chatInit.current && r.data.total > prevChatUnread.current) toast.info("New message in a work chat");
      prevChatUnread.current = r.data.total; chatInit.current = true;
      setChatUnread(r.data);
    }).catch(() => {});
    if (spinner) setLoading(false);
  };
  useEffect(() => { load(true); }, []);

  // Real-time auto-refresh
  useEffect(() => {
    const iv = setInterval(() => load(false), 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, []);

  const copyReferral = async () => { try { await navigator.clipboard.writeText(referral.referral_code); toast.success("Referral code copied"); } catch (e) { toast.error("Could not copy"); } };
  const daysLeft = payInfo.subscription_expires ? Math.ceil((new Date(payInfo.subscription_expires) - new Date()) / 86400000) : null;

  const subscribe = async () => {
    await api.post("/broker/subscribe");
    toast.success("Subscription request created — pay via UPI, admin will confirm.");
    load();
  };

  const [promo, setPromo] = useState("");
  const applyPromo = async () => {
    if (!promo.trim()) { return; }
    try {
      await api.post("/promo/validate", { code: promo });
      if (!isOwner) { await api.post("/broker/subscribe"); }
      const { data } = await api.post("/payments/apply-promo", { code: promo });
      toast.success(`Promo code applied to ${data.applied} payment(s)`); setPromo(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply code"); }
  };

  const createOffer = async () => {
    if (!offerForm.title) { toast.error("Offer title is required"); return; }
    await api.post("/offers", offerForm);
    toast.success("Offer submitted for admin approval");
    setOfferForm({ title: "", description: "", coupon_code: "", discount: "", area_name: "", pincode: "", city: "" });
    load();
  };
  const deleteOffer = async (id) => {
    await api.delete(`/offers/${id}`);
    toast.success("Offer deleted");
    load();
  };

  const submitRespond = async () => {
    if (!respText.trim()) { toast.error("Please add a response"); return; }
    await api.put(`/enquiries/${respondEnq.id}/respond`, { response: respText, response_date: respDate });
    toast.success("Response sent — admin will alert the customer");
    setRespondEnq(null); setRespText(""); setRespDate("");
    load();
  };

  const active = props.filter((p) => p.status !== "sold");
  const sold = props.filter((p) => p.status === "sold");

  const del = async (id) => {
    await api.delete(`/properties/${id}`);
    toast.success("Property deleted");
    load();
  };
  const setStatus = async (id, status) => {
    await api.put(`/properties/${id}/status`, null, { params: { status } });
    toast.success(status === "sold" ? "Marked as sold" : "Marked active");
    load();
  };

  const Card = ({ p }) => (
    <div className="bg-card border border-brand-line rounded-2xl overflow-hidden" data-testid={`broker-prop-${p.id}`}>
      <div className="relative aspect-[16/9] bg-secondary">
        {p.photos?.[0] && <img src={fileUrl(p.photos[0])} alt="" className="w-full h-full object-cover" />}
        <div className="absolute top-3 right-3 flex gap-2">
          <button onClick={() => navigate(`/broker/property/${p.id}/edit`)} data-testid={`edit-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow hover:bg-white text-brand-emerald">
            <Pencil className="w-4 h-4" />
          </button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid={`delete-${p.id}`} className="w-9 h-9 rounded-full bg-white/95 flex items-center justify-center shadow hover:bg-white text-brand-terracotta"><Trash2 className="w-4 h-4" /></button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader><AlertDialogTitle>Delete this property?</AlertDialogTitle><AlertDialogDescription>This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("form.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={() => del(p.id)} className="bg-brand-terracotta">{t("broker.delete")}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
        <span className="absolute bottom-3 left-3 bg-white/95 text-brand-ink font-heading font-bold px-3 py-1 rounded-lg text-sm">{formatPrice(p.price, p.price_unit)}</span>
      </div>
      <div className="p-4">
        <h3 className="font-heading font-semibold text-brand-ink line-clamp-1">{p.title}</h3>
        <p className="flex items-center gap-1 text-sm text-brand-muted mt-1"><MapPin className="w-3.5 h-3.5" /> {p.area_name}, {p.city}</p>
        <p className="flex items-center gap-1 text-sm text-brand-muted mt-1" data-testid={`broker-views-${p.id}`}><Eye className="w-3.5 h-3.5" /> {p.views || 0} views</p>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {p.approved === false && <span className="text-[11px] font-semibold text-brand-lease bg-brand-lease/10 rounded-md px-2 py-1" data-testid={`prop-pending-${p.id}`}>Pending approval</span>}
          {p.approved && <span className="text-[11px] font-semibold text-brand-sale bg-brand-sale/10 rounded-md px-2 py-1">Approved · Live</span>}
          {p.payment_confirmed === false && <span className="text-[11px] font-semibold text-brand-terracotta bg-brand-terracotta/10 rounded-md px-2 py-1 inline-flex items-center gap-1" data-testid={`prop-unpaid-${p.id}`}><AlertCircle className="w-3 h-3" /> Awaiting payment</span>}
        </div>
        {p.last_modified_by === "admin" || p.last_modified_by === "subadmin" ? (
          <div className="mt-2 text-xs font-medium text-brand-rent bg-brand-rent/10 rounded-lg px-2.5 py-1.5 inline-flex items-center gap-1" data-testid={`admin-updated-${p.id}`}>
            <Clock className="w-3.5 h-3.5" /> {t("track.adminUpdated")}{p.last_modified_at ? " · " + new Date(p.last_modified_at).toLocaleString() : ""}
          </div>
        ) : null}
        {p.admin_locked && <div className="mt-1 text-[11px] text-brand-terracotta">{t("track.needsApproval")}</div>}
        <div className="mt-3 flex gap-2">
          {p.status !== "sold" ? (
            <Button size="sm" onClick={() => setStatus(p.id, "sold")} className="rounded-full bg-brand-emerald flex-1" data-testid={`mark-sold-${p.id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> {t("broker.markSold")}</Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setStatus(p.id, "available")} className="rounded-full flex-1" data-testid={`mark-active-${p.id}`}><RotateCcw className="w-4 h-4 mr-1" /> {t("broker.markActive")}</Button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl text-brand-ink tracking-tight">{isOwner ? "Owner Dashboard" : t("broker.dashboard")}</h1>
          <div className="flex items-center gap-2 text-brand-emerald font-medium mt-1">
            <BadgeCheck className="w-4 h-4" /> {isOwner ? `Individual Owner · ${user.name}` : `${user.badge_number || "No badge assigned yet"} · ${user.name}`}
          </div>
          {user.profile_modified_by === "admin" && (
            <div className="mt-2 text-xs text-brand-rent bg-brand-rent/10 rounded-lg px-3 py-1.5 inline-flex items-center gap-1" data-testid="profile-admin-note">
              <Clock className="w-3.5 h-3.5" /> Your profile was updated by admin{user.profile_modified_at ? " on " + new Date(user.profile_modified_at).toLocaleDateString() : ""}. Property edits now need admin approval.
            </div>
          )}
        </div>
        <Button onClick={() => navigate("/broker/property/new")} className="rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-1.5" /> {t("broker.addProperty")}
        </Button>
      </div>

      {!isOwner && daysLeft !== null && daysLeft <= 7 && (
        <div className={`mb-5 rounded-xl px-4 py-3 flex items-center gap-3 text-sm ${daysLeft < 0 ? "bg-brand-terracotta/10 text-brand-terracotta" : "bg-brand-lease/10 text-brand-lease"}`} data-testid="renewal-reminder">
          <BellRing className="w-5 h-5 shrink-0" />
          <span className="flex-1 font-medium">
            {daysLeft < 0
              ? `Your subscription expired ${Math.abs(daysLeft)} day(s) ago — renew to keep your listings live.`
              : `Your subscription expires in ${daysLeft} day(s) (${new Date(payInfo.subscription_expires).toLocaleDateString()}). Renew to avoid interruption.`}
          </span>
          <Button size="sm" className="rounded-full bg-brand-emerald shrink-0" onClick={subscribe} data-testid="renew-btn"><IndianRupee className="w-4 h-4 mr-1" />Renew ₹{settings.monthly_subscription}</Button>
        </div>
      )}

      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-brand-emerald" /></div> : (
        <>
        <div className="md:hidden mb-4">
          <Button variant="outline" onClick={() => setNavOpen(true)} data-testid="broker-nav-toggle" className="rounded-full"><Menu className="w-4 h-4 mr-1.5" /> Menu</Button>
        </div>
        <Tabs value={tab} onValueChange={setTab} orientation="vertical" className="flex flex-col md:flex-row gap-6 md:items-stretch md:h-[calc(100vh-11rem)]">
          {navOpen && <div className="fixed inset-0 bg-black/40 z-40 md:hidden" onClick={() => setNavOpen(false)} data-testid="broker-nav-overlay" />}
          <TabsList onClick={() => setNavOpen(false)} className={`h-auto flex flex-col gap-1 p-2 bg-card border border-brand-line overflow-y-auto items-stretch justify-start [&>button]:w-full [&>button]:justify-start [&>button]:shrink-0 fixed top-0 left-0 bottom-0 z-50 w-64 rounded-none transform transition-transform duration-300 ${navOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 md:static md:z-auto md:w-56 md:h-full md:rounded-2xl`}>
            <TabsTrigger value="active" data-testid="tab-active">{t("broker.active")} ({active.length})</TabsTrigger>
            <TabsTrigger value="sold" data-testid="tab-sold">{t("broker.sold")} ({sold.length})</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="tab-enquiries">{t("broker.enquiries")} ({enquiries.length}){chatUnread.total > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-terracotta text-white text-[10px] font-bold inline-flex items-center justify-center" data-testid="broker-chat-unread">{chatUnread.total}</span>}</TabsTrigger>
            <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
            {!isOwner && <TabsTrigger value="offers" data-testid="tab-offers">{t("offers.tab")} ({offers.length})</TabsTrigger>}
          </TabsList>

          <div className="flex-1 min-w-0 w-full md:h-full md:overflow-y-auto pr-0 md:pr-1">
          <div className="sticky top-0 z-10 bg-brand-page/95 backdrop-blur pb-3 mb-1 border-b border-brand-line md:pt-1">
            <h2 className="font-heading font-bold text-xl sm:text-2xl text-brand-ink tracking-tight" data-testid="active-section-title">{SECTION_TITLES[tab] || "Dashboard"}</h2>
          </div>

          <TabsContent value="active" className="mt-6">
            {active.length === 0 ? <p className="text-brand-muted py-12 text-center">{t("broker.noProps")}</p> : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{active.map((p) => <Card key={p.id} p={p} />)}</div>
            )}
          </TabsContent>
          <TabsContent value="sold" className="mt-6">
            {sold.length === 0 ? <p className="text-brand-muted py-12 text-center">No sold properties yet.</p> : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{sold.map((p) => <Card key={p.id} p={p} />)}</div>
            )}
          </TabsContent>
          <TabsContent value="enquiries" className="mt-6">
            {enquiries.length === 0 ? (
              <div className="text-center py-12 text-brand-muted"><Inbox className="w-10 h-10 mx-auto mb-3 opacity-50" />No enquiries assigned yet. Admin assigns customer enquiries to you.</div>
            ) : (
              <div className="space-y-3">
                {enquiries.map((e) => (
                  <div key={e.id} className="bg-card border border-brand-line rounded-xl p-4" data-testid={`broker-enq-${e.id}`}>
                    <div className="flex flex-wrap justify-between gap-3">
                      <div>
                        <div className="font-semibold text-brand-ink">{e.property_title} {e.interest_type && <span className="text-xs bg-brand-emerald/10 text-brand-emerald px-2 py-0.5 rounded-full ml-1 capitalize">{e.interest_type}</span>}</div>
                        <div className="text-sm text-brand-muted">{e.customer_name} · {e.customer_phone}</div>
                        {e.message && <div className="text-sm text-brand-ink/70 mt-1">"{e.message}"</div>}
                        {e.due_date && <div className="text-xs text-brand-terracotta mt-1 flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" /> {t("enq.due")}: {new Date(e.due_date).toLocaleDateString()}</div>}
                        {e.broker_response && <div className="text-sm mt-2 bg-secondary rounded-lg p-2"><span className="text-xs text-brand-muted">{t("enq.response")}: </span>{e.broker_response}{e.response_date && ` (${e.response_date})`}</div>}
                      </div>
                      <span className={`text-xs font-medium px-3 py-1 rounded-full h-fit capitalize ${e.status === "confirmed" ? "bg-brand-sale/10 text-brand-sale" : e.status === "assigned" ? "bg-brand-lease/10 text-brand-lease" : "bg-brand-emerald/10 text-brand-emerald"}`}>{e.status}</span>
                    </div>
                    {e.assigned_broker_id === user.id && (e.status === "assigned" || e.status === "responded") && (
                      <Button size="sm" className="rounded-full bg-brand-emerald mt-3" onClick={() => { setRespondEnq(e); setRespText(e.broker_response || ""); setRespDate(e.response_date || ""); }} data-testid={`respond-${e.id}`}><Send className="w-4 h-4 mr-1" /> {t("enq.respond")}</Button>
                    )}
                    {e.assigned_broker_id === user.id && feature("assignment_chat") && (
                      <>
                        {chatUnread.by_enquiry?.[e.id] > 0 && <div className="mt-2 text-xs font-semibold text-brand-terracotta" data-testid={`broker-enq-unread-${e.id}`}>{chatUnread.by_enquiry[e.id]} new message(s)</div>}
                        <AssignmentChat enquiryId={e.id} />
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="payments" className="mt-6">
            <div className="grid lg:grid-cols-[380px_1fr] gap-6">
              <div className="bg-card border border-brand-line rounded-2xl p-6 h-fit">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-2"><QrCode className="w-5 h-5 text-brand-emerald" /> {isOwner ? "Pay per Listing" : "Monthly Subscription"}</h3>
                <p className="text-sm text-brand-muted mb-4">
                  {isOwner
                    ? `Individual owners pay ₹${settings.per_listing_fee} per property listing. Scan & pay, then admin confirms to publish.`
                    : `Brokers pay ₹${settings.monthly_subscription}/month for unlimited listings. Scan & pay, then admin activates your subscription.`}
                </p>
                {!isOwner && (
                  <div className={`rounded-xl p-3 mb-4 text-sm ${payInfo.subscription_active ? "bg-brand-sale/10 text-brand-sale" : "bg-brand-lease/10 text-brand-lease"}`} data-testid="subscription-status">
                    {payInfo.subscription_active
                      ? `Active subscription${payInfo.subscription_expires ? " · expires " + new Date(payInfo.subscription_expires).toLocaleDateString() : ""}`
                      : "No active subscription — subscribe to publish new listings and keep unlimited listings live."}
                  </div>
                )}
                {settings.upi_qr ? (
                  <img src={fileUrl(settings.upi_qr)} alt="UPI QR" className="w-44 h-44 bg-white rounded-xl p-2 mx-auto border border-brand-line" data-testid="broker-pay-qr" />
                ) : (
                  <div className="w-44 h-44 bg-secondary rounded-xl mx-auto flex items-center justify-center text-brand-muted text-sm text-center px-3">QR will be shared by admin</div>
                )}
                <div className="mt-3 bg-secondary rounded-xl p-3 text-center">
                  <div className="text-xs text-brand-muted">UPI ID</div>
                  <div className="font-heading font-semibold text-brand-ink">{settings.upi_id || "—"}</div>
                </div>
                {!isOwner && !payInfo.subscription_active && (
                  <Button onClick={subscribe} className="w-full rounded-full bg-brand-emerald mt-4" data-testid="subscribe-btn"><IndianRupee className="w-4 h-4 mr-1.5" /> Request Subscription (₹{settings.monthly_subscription})</Button>
                )}
                {(isOwner || !payInfo.subscription_active) && (
                  <div className="mt-4" data-testid="promo-apply-box">
                    <p className="text-xs text-brand-muted mb-1.5">Have a promo code? Apply it before you pay.</p>
                    <div className="flex gap-2">
                      <Input value={promo} onChange={(e) => setPromo(e.target.value.toUpperCase())} placeholder="PROMO CODE" data-testid="promo-apply-input" />
                      <Button variant="outline" className="rounded-full shrink-0" onClick={applyPromo} data-testid="promo-apply-btn">Apply</Button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                {!isOwner && feature("broker_referrals") && (
                  <div className="bg-card border border-brand-line rounded-2xl p-5 mb-6" data-testid="referral-card">
                    <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-2"><Gift className="w-5 h-5 text-brand-terracotta" /> Referral Program</h3>
                    <p className="text-sm text-brand-muted mb-3">Share your code with other brokers. When they sign up with it, they're linked to you.</p>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-secondary rounded-xl px-4 py-3 font-heading font-bold text-brand-emerald tracking-wider" data-testid="referral-code">{referral.referral_code || "—"}</div>
                      <Button variant="outline" className="rounded-full" onClick={copyReferral} data-testid="copy-referral"><Copy className="w-4 h-4 mr-1" />Copy</Button>
                    </div>
                    <div className="mt-3 text-sm text-brand-ink">Brokers referred: <span className="font-bold text-brand-emerald" data-testid="referral-count">{referral.count}</span></div>
                    {referral.referrals.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {referral.referrals.map((r, i) => (
                          <div key={i} className="text-xs text-brand-muted flex items-center gap-1.5"><BadgeCheck className="w-3.5 h-3.5 text-brand-emerald" />{r.name} {r.badge_number && `· ${r.badge_number}`}</div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <h3 className="font-heading font-semibold text-lg mb-4">Payment History</h3>
                {payInfo.payments.length === 0 ? (
                  <div className="text-center py-12 text-brand-muted"><IndianRupee className="w-10 h-10 mx-auto mb-3 opacity-50" />No payments yet.</div>
                ) : (
                  <div className="space-y-3">
                    {payInfo.payments.map((p) => (
                      <div key={p.id} className="bg-card border border-brand-line rounded-xl p-4 flex flex-wrap justify-between gap-3" data-testid={`payment-${p.id}`}>
                        <div>
                          <div className="font-semibold text-brand-ink capitalize">{p.kind === "subscription" ? "Monthly Subscription" : p.property_title || "Listing Fee"}</div>
                          <div className="text-sm text-brand-muted">{p.promo_code && <span className="line-through mr-1">₹{p.original_amount}</span>}₹{p.amount}{p.promo_code && ` (${p.promo_code})`} · {new Date(p.created_at).toLocaleDateString()}</div>
                        </div>
                        <span className={`text-xs font-medium px-3 py-1 rounded-full h-fit ${p.status === "confirmed" ? "bg-brand-sale/10 text-brand-sale" : "bg-brand-lease/10 text-brand-lease"}`}>{p.status === "confirmed" ? "Confirmed" : "Pending confirmation"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="offers" className="mt-6">
            <div className="grid lg:grid-cols-[380px_1fr] gap-6">
              <div className="bg-card border border-brand-line rounded-2xl p-5 h-fit">
                <h3 className="font-heading font-semibold text-lg flex items-center gap-2 mb-4"><BadgePercent className="w-5 h-5 text-brand-terracotta" /> {t("offers.create")}</h3>
                <div className="space-y-3">
                  <div><Label>{t("offers.newTitle")}</Label><Input value={offerForm.title} onChange={(e) => setOfferForm((f) => ({ ...f, title: e.target.value }))} className="mt-1.5" data-testid="offer-title" /></div>
                  <div><Label>{t("form.desc")}</Label><Textarea value={offerForm.description} onChange={(e) => setOfferForm((f) => ({ ...f, description: e.target.value }))} className="mt-1.5" data-testid="offer-desc" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>{t("offers.couponCode")}</Label><Input value={offerForm.coupon_code} onChange={(e) => setOfferForm((f) => ({ ...f, coupon_code: e.target.value }))} placeholder="SAVE10" className="mt-1.5" data-testid="offer-code" /></div>
                    <div><Label>{t("offers.discount")}</Label><Input value={offerForm.discount} onChange={(e) => setOfferForm((f) => ({ ...f, discount: e.target.value }))} placeholder="10% off" className="mt-1.5" data-testid="offer-discount" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>{t("form.pincode")}</Label><Input value={offerForm.pincode} onChange={(e) => setOfferForm((f) => ({ ...f, pincode: e.target.value }))} className="mt-1.5" data-testid="offer-pincode" /></div>
                    <div><Label>{t("form.areaName")}</Label><Input value={offerForm.area_name} onChange={(e) => setOfferForm((f) => ({ ...f, area_name: e.target.value }))} className="mt-1.5" data-testid="offer-area" /></div>
                    <div><Label>{t("form.city")}</Label><Input value={offerForm.city} onChange={(e) => setOfferForm((f) => ({ ...f, city: e.target.value }))} className="mt-1.5" data-testid="offer-city" /></div>
                  </div>
                  <p className="text-xs text-brand-muted">Leave area blank to target all locations. Admin must approve before it shows to customers.</p>
                  <Button onClick={createOffer} className="w-full rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover" data-testid="offer-submit"><Plus className="w-4 h-4 mr-1.5" /> {t("offers.create")}</Button>
                </div>
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg mb-4">{t("offers.myOffers")}</h3>
                {offers.length === 0 ? (
                  <div className="text-center py-12 text-brand-muted"><Ticket className="w-10 h-10 mx-auto mb-3 opacity-50" />No offers yet. Create one and it goes to admin for approval.</div>
                ) : (
                  <div className="space-y-3">
                    {offers.map((o) => (
                      <div key={o.id} className="bg-card border border-brand-line rounded-xl p-4 flex flex-wrap justify-between gap-3" data-testid={`broker-offer-${o.id}`}>
                        <div>
                          <div className="font-semibold text-brand-ink flex items-center gap-2">{o.title} {o.discount && <span className="text-xs bg-brand-sale/10 text-brand-sale px-2 py-0.5 rounded-full">{o.discount}</span>}</div>
                          <div className="text-sm text-brand-muted">{o.coupon_code && `Code: ${o.coupon_code} · `}{o.area_name || o.city || "All areas"}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-medium px-3 py-1 rounded-full h-fit ${o.status === "pushed" ? "bg-brand-sale/10 text-brand-sale" : o.status === "rejected" ? "bg-brand-terracotta/10 text-brand-terracotta" : "bg-brand-lease/10 text-brand-lease"}`}>
                            {o.status === "pushed" ? t("offers.pushed") : o.status === "rejected" ? t("offers.rejected") : t("offers.pendingPush")}
                          </span>
                          <button onClick={() => deleteOffer(o.id)} data-testid={`delete-offer-${o.id}`} className="text-brand-terracotta"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </div>
        </Tabs>
        </>
      )}

      <Dialog open={!!respondEnq} onOpenChange={(o) => !o && setRespondEnq(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("enq.respond")}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t("enq.response")}</Label>
              <Textarea value={respText} onChange={(e) => setRespText(e.target.value)} placeholder="Property is available, we can schedule a visit..." className="mt-1.5" data-testid="respond-text" />
            </div>
            <div>
              <Label>{t("enq.responseDate")}</Label>
              <Input type="date" value={respDate} onChange={(e) => setRespDate(e.target.value)} className="mt-1.5" data-testid="respond-date" />
            </div>
            <p className="text-xs text-brand-muted">Your response goes to admin, who alerts the customer (within 3 working days).</p>
            <Button onClick={submitRespond} className="w-full rounded-full bg-brand-emerald" data-testid="respond-submit"><Send className="w-4 h-4 mr-1.5" /> {t("enq.respond")}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
